import React, { useEffect, useRef, useState } from 'react';
import { useGameStore } from '@store/gameStore';
import type { Color } from '@/types';
import styles from './Timer.module.css';

interface TimerProps {
  color: Color;
}

export const Timer: React.FC<TimerProps> = ({ color }) => {
  const gameState = useGameStore((state) => state.gameState);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastUpdateRef = useRef<number>(Date.now());
  
  useEffect(() => {
    if (!gameState) return;
    
    const timer = gameState.timers[color];
    setTimeRemaining(timer.remaining);
    
    // Only run client-side timer if this player's timer is running
    // This provides smooth countdown between server updates
    if (timer.isRunning && timer.remaining > 0 && gameState.currentTurn === color) {
      lastUpdateRef.current = Date.now();
      
      // Reduced frequency - server sends updates every second
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = now - lastUpdateRef.current;
        lastUpdateRef.current = now;
        
        setTimeRemaining(prev => Math.max(0, prev - elapsed));
      }, 200); // Update every 200ms for smoother display
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [gameState, color]);
  
  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    
    if (ms < 10000 && ms > 0) {
      const tenths = Math.floor((ms % 1000) / 100);
      return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
    }
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  const isActive = gameState?.currentTurn === color && gameState.gameStatus === 'active';
  const isCritical = timeRemaining < 10000;
  const isWarning = timeRemaining < 30000;
  
  const timerClasses = [
    styles.timer,
    isActive && styles.active,
    isCritical && styles.critical,
    !isCritical && isWarning && styles.warning,
  ].filter(Boolean).join(' ');
  
  return (
    <div className={timerClasses}>
      <div className={styles.player}>
        <div className={`${styles.colorIndicator} ${styles[color]}`} />
        <span className={styles.colorName}>{color}</span>
      </div>
      <div className={styles.time}>
        {formatTime(timeRemaining)}
      </div>
    </div>
  );
};